import React, { useState } from 'react';
import {
  Card, CardContent, Typography, Button, Box, CircularProgress, useMediaQuery, IconButton, Tooltip
} from '@mui/material';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { motion, useMotionValue } from 'framer-motion';

const SongCard = ({ song, handleDelete, loading, isNew, showTableNumber }) => {
  const songKey = `${song.fields['Song Name']} - ${song.fields['Artist']}`;
  const [isDragging, setIsDragging] = useState(false);
  const isMobile = useMediaQuery('(max-width:600px)');

  // Motion value for x-axis dragging
  const x = useMotionValue(0);

  const [copied, setCopied] = useState(false);

  const handleCopy = (e) => {
    e.stopPropagation();
    const textToCopy = `${song.fields['Song Name']} - ${song.fields['Artist']}`;
    navigator.clipboard.writeText(textToCopy).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleButtonClick = async (action) => {
    if (!loading) {  // Prevenir múltiples clicks mientras está cargando
      await handleDelete(songKey, action);
    }
  };

  const handleDragEnd = (event, info) => {
    if (info.offset.x < -100) {
      handleButtonClick('No la quiero poner');
    }
    setIsDragging(false);
  };

  return (
    <motion.div
      drag="x"
      dragConstraints={{ left: 0, right: 0 }}
      onDragStart={() => setIsDragging(true)}
      onDragEnd={handleDragEnd}
      style={{
        cursor: isDragging ? 'grabbing' : 'grab',
        x,
      }}
      whileTap={{ scale: 0.98 }}
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: loading ? 0 : 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ duration: 0.4, type: "spring" }}
    >
      <Card
        component={motion.div}
        sx={{
          borderRadius: '24px',
          position: 'relative',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          minHeight: '160px',
          background: 'rgba(30, 30, 30, 0.6)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          boxShadow: isNew
            ? '0 8px 32px 0 rgba(213, 0, 0, 0.15), 0 0 0 1px rgba(213, 0, 0, 0.3)'
            : '0 8px 32px 0 rgba(0, 0, 0, 0.3)',
          overflow: 'visible', // Allow badges to pop out if needed
          transition: 'all 0.3s ease',
          '&:hover': {
            background: 'rgba(40, 40, 40, 0.7)',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            transform: 'translateY(-2px)',
            boxShadow: '0 12px 40px 0 rgba(0, 0, 0, 0.4)'
          }
        }}
      >
        {/* New Indicator */}
        {isNew && (
          <Box
            sx={{
              position: 'absolute',
              top: -10,
              left: 20,
              background: 'linear-gradient(135deg, #d50000 0%, #ff1744 100%)',
              color: '#fff',
              px: 1.5,
              py: 0.5,
              borderRadius: '12px',
              fontSize: '0.75rem',
              fontWeight: 'bold',
              boxShadow: '0 4px 12px rgba(213, 0, 0, 0.4)',
              zIndex: 2,
              letterSpacing: '0.5px'
            }}
          >
            NUEVO
          </Box>
        )}

        {/* Hot Indicator */}
        {song.count > 1 && (
          <Box
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              display: 'flex',
              alignItems: 'center',
              background: 'rgba(255, 87, 34, 0.15)',
              border: '1px solid rgba(255, 87, 34, 0.3)',
              borderRadius: '20px',
              padding: '4px 8px',
              color: '#ff5722'
            }}
          >
            <WhatshotIcon sx={{ fontSize: '1.2rem', mr: 0.5 }} />
            <Typography variant="body2" sx={{ fontWeight: '800', fontSize: '0.9rem' }}>
              x{song.count}
            </Typography>
          </Box>
        )}

        <CardContent sx={{ flexGrow: 1, pt: 4, pb: 2, px: 3 }}>
          <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
            {song.fields['Album Cover'] && (
              <Box
                component="img"
                src={song.fields['Album Cover']}
                alt={song.fields['Song Name']}
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '12px',
                  objectFit: 'cover',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                }}
              />
            )}
            <Box
              onPointerDown={(e) => e.stopPropagation()}
              sx={{ flex: 1, minWidth: 0, cursor: 'text', userSelect: 'text' }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  fontSize: '1.1rem',
                  color: '#fff',
                  lineHeight: 1.2,
                  mb: 0.5,
                  textShadow: '0 2px 4px rgba(0,0,0,0.3)'
                }}
              >
                {song.fields['Song Name']}
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontWeight: 500,
                  fontSize: '0.9rem'
                }}
              >
                {song.fields['Artist']}
              </Typography>
            </Box>
            <Tooltip title={copied ? "¡Copiado!" : "Copiar nombre y artista"}>
              <IconButton
                onClick={handleCopy}
                onPointerDown={(e) => e.stopPropagation()}
                sx={{
                  color: copied ? '#69f0ae' : 'rgba(255,255,255,0.5)',
                  '&:hover': {
                    color: copied ? '#69f0ae' : '#fff',
                    backgroundColor: 'rgba(255,255,255,0.1)'
                  }
                }}
              >
                {copied ? <CheckCircleOutlineIcon /> : <ContentCopyIcon />}
              </IconButton>
            </Tooltip>
          </Box>

          {showTableNumber && song.fields['Table Number'] && (
            <Box
              sx={{
                display: 'inline-block',
                mt: 1.5,
                px: 1.5,
                py: 0.5,
                borderRadius: '8px',
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontWeight: 600,
                  fontSize: '0.85rem'
                }}
              >
                Mesa: {song.fields['Table Number']}
              </Typography>
            </Box>
          )}
        </CardContent>

        <Box sx={{ p: 2, pt: 0, display: 'flex', gap: 1.5 }}>
          {loading ? (
            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', py: 1 }}>
              <CircularProgress size={24} sx={{ color: 'rgba(255,255,255,0.5)' }} />
            </Box>
          ) : (
            <>
              <Button
                variant="contained"
                disabled={loading}
                onClick={() => handleButtonClick('No la quiero poner')}
                sx={{
                  flex: 1,
                  borderRadius: '16px',
                  py: 1.2,
                  fontSize: isMobile ? '0.85rem' : '0.9rem',
                  fontWeight: 700,
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                  backgroundColor: 'rgba(244, 66, 54, 0.15)',
                  color: '#ff5252',
                  border: '1px solid rgba(244, 66, 54, 0.3)',
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: 'rgba(244, 66, 54, 0.25)',
                    border: '1px solid rgba(244, 66, 54, 0.5)',
                    boxShadow: '0 4px 12px rgba(244, 66, 54, 0.2)'
                  },
                  '&:disabled': {
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    color: 'rgba(255,255,255,0.3)',
                    border: 'none'
                  }
                }}
              >
                <CloseRoundedIcon sx={{ mr: 0.5, fontSize: '1.2rem' }} />
                No la pondré
              </Button>
              <Button
                variant="contained"
                disabled={loading}
                onClick={() => handleButtonClick('Se puso')}
                sx={{
                  flex: 1,
                  borderRadius: '16px',
                  py: 1.2,
                  fontSize: isMobile ? '0.85rem' : '0.9rem',
                  fontWeight: 700,
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                  backgroundColor: 'rgba(84, 167, 115, 0.2)',
                  color: '#69f0ae',
                  border: '1px solid rgba(84, 167, 115, 0.3)',
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: 'rgba(84, 167, 115, 0.3)',
                    border: '1px solid rgba(84, 167, 115, 0.5)',
                    boxShadow: '0 4px 12px rgba(84, 167, 115, 0.2)'
                  },
                  '&:disabled': {
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    color: 'rgba(255,255,255,0.3)',
                    border: 'none'
                  }
                }}
              >
                <CheckRoundedIcon sx={{ mr: 0.5, fontSize: '1.2rem' }} />
                Ya la puse
              </Button>
            </>
          )}
        </Box>
      </Card>
    </motion.div>
  );
};

export default SongCard;
